import { useEffect, useState } from 'react'
import { Bell, TrendingUp, TrendingDown, Zap, Target, X, ExternalLink, BellRing, BellOff, ChevronRight, CheckCircle2, AlertTriangle } from 'lucide-react'
import clsx from 'clsx'
import { useStore } from '../../store/useStore'
import type { Alert } from '../../types'

type NotifPermission = 'granted' | 'denied' | 'default' | 'unsupported'

function useTelegramStatus() {
  const [configured, setConfigured] = useState<boolean | null>(null)
  const [link, setLink] = useState<string | null>(null)

  useEffect(() => {
    fetch('/api/telegram/status')
      .then((r) => r.json())
      .then((d) => {
        setConfigured(d.configured)
        setLink(d.link ?? null)
      })
      .catch(() => setConfigured(false))
  }, [])

  return { configured, link }
}

function useNotificationPermission(): [NotifPermission, () => void] {
  const supported = typeof window !== 'undefined' && 'Notification' in window
  const [permission, setPermission] = useState<NotifPermission>(
    supported ? Notification.permission : 'unsupported',
  )

  useEffect(() => {
    if (!supported) return
    setPermission(Notification.permission)
  }, [supported])

  function request() {
    if (!supported || permission === 'granted') return
    Notification.requestPermission().then((p) => setPermission(p)).catch(() => {})
  }

  return [permission, request]
}

function getRelativeTime(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime()
  const seconds = Math.floor(diff / 1000)
  if (seconds < 60) return `${seconds}秒前`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}分前`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}時間前`
  return `${Math.floor(hours / 24)}日前`
}

function AlertIcon({ type }: { type: Alert['type'] }) {
  const cls = 'flex-none w-4 h-4'
  if (type === 'rise_signal')     return <TrendingUp  className={clsx(cls, 'text-trading-green')}  />
  if (type === 'fall_signal')     return <TrendingDown className={clsx(cls, 'text-trading-red')}   />
  if (type === 'ai_entry_signal') return <Target       className={clsx(cls, 'text-trading-blue')}  />
  return <Zap className={clsx(cls, 'text-trading-yellow')} />
}

function AlertBadge({ type, severity, message }: { type: Alert['type']; severity: Alert['severity']; message: string }) {
  const base = 'ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap'
  const strength = severity === 'critical' ? '高' : severity === 'warning' ? '中' : '低'

  if (type === 'ai_entry_signal') {
    const isBuy  = /\(BUY\)/i.test(message)
    const isSell = /\(SELL\)/i.test(message)
    if (isBuy)  return <span className={clsx(base, 'bg-trading-green/20 text-trading-green')}>AI BUY</span>
    if (isSell) return <span className={clsx(base, 'bg-trading-red/20 text-trading-red')}>AI SELL</span>
    return <span className={clsx(base, 'bg-trading-blue/20 text-trading-blue')}>AI シグナル</span>
  }
  if (type === 'rise_signal')
    return <span className={clsx(base, 'bg-trading-green/20 text-trading-green')}>↑ {strength}</span>
  if (type === 'fall_signal')
    return <span className={clsx(base, 'bg-trading-red/20 text-trading-red')}>↓ {strength}</span>
  if (type === 'volatility_spike')
    return <span className={clsx(base, 'bg-trading-yellow/20 text-trading-yellow')}>⚡ 急変動</span>
  return <span className={clsx(base, 'bg-trading-blue/20 text-trading-blue')}>📅 経済指標</span>
}

const SEVERITY_BORDER: Record<Alert['severity'], string> = {
  info: 'border-l-trading-blue',
  warning: 'border-l-trading-yellow',
  critical: 'border-l-trading-red',
}

const SEVERITY_BG: Record<Alert['severity'], string> = {
  info: 'bg-trading-blue/5',
  warning: 'bg-trading-yellow/5',
  critical: 'bg-trading-red/5',
}

interface Props {
  /** Called after navigating to a symbol (e.g. close the mobile drawer) */
  onNavigate?: () => void
  /** Called when the close button is tapped (mobile drawer only) */
  onClose?: () => void
}

export default function AlertPanel({ onNavigate, onClose }: Props) {
  const alerts            = useStore((s) => s.alerts)
  const dismissedAlertIds = useStore((s) => s.dismissedAlertIds)
  const dismissAlert      = useStore((s) => s.dismissAlert)
  const dismissAllAlerts  = useStore((s) => s.dismissAllAlerts)
  const setSelectedSymbol = useStore((s) => s.setSelectedSymbol)
  const setActiveTab      = useStore((s) => s.setActiveTab)
  const [notifPermission, requestNotif] = useNotificationPermission()
  const { configured: tgConfigured, link: tgLink } = useTelegramStatus()

  const visibleAlerts = alerts.filter((a) => !dismissedAlertIds.has(a.id))

  function navigateToSymbol(symbol: string, alertType: Alert['type']) {
    setSelectedSymbol(symbol)
    setActiveTab(alertType === 'ai_entry_signal' ? 'analysis' : 'dashboard')
    onNavigate?.()
  }

  return (
    <div className="flex flex-col h-full w-full bg-trading-panel">
      {/* Header */}
      <div className="flex-none flex items-center justify-between px-4 py-3 border-b border-trading-border">
        <div className="flex items-center gap-2">
          {onClose && (
            <button
              onClick={onClose}
              className="text-trading-muted hover:text-trading-text transition-colors mr-1"
              title="閉じる"
            >
              <ChevronRight size={16} />
            </button>
          )}
          <Bell size={14} className="text-trading-muted" />
          <span className="text-xs font-semibold uppercase tracking-wider text-trading-text">
            通知
          </span>
          {visibleAlerts.length > 0 && (
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-trading-red text-white">
              {visibleAlerts.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {notifPermission !== 'unsupported' && notifPermission !== 'denied' && (
            <button
              onClick={requestNotif}
              title={notifPermission === 'granted' ? 'ブラウザ通知: 許可済み' : 'ブラウザ通知を許可する'}
              className={clsx(
                'transition-colors',
                notifPermission === 'granted'
                  ? 'text-trading-green cursor-default'
                  : 'text-trading-muted hover:text-trading-blue',
              )}
            >
              {notifPermission === 'granted' ? <BellRing size={13} /> : <BellOff size={13} />}
            </button>
          )}
          {visibleAlerts.length > 0 && (
            <button
              onClick={dismissAllAlerts}
              className="text-[10px] text-trading-muted hover:text-trading-text transition-colors"
            >
              全消去
            </button>
          )}
        </div>
      </div>

      {/* Telegram status bar */}
      {tgConfigured !== null && (
        <div
          onClick={() => {
            if (tgConfigured && tgLink) {
              window.open(tgLink, '_blank', 'noopener,noreferrer')
            }
          }}
          className={clsx(
            'flex-none flex items-center px-4 py-2 border-b border-trading-border text-[11px]',
            tgConfigured ? 'bg-trading-green/5' : 'bg-trading-yellow/5',
            tgConfigured && tgLink && 'cursor-pointer hover:brightness-125',
          )}
          title={tgConfigured && tgLink ? `${tgLink} を開く` : undefined}
        >
          <div className="flex items-center gap-1.5">
            {tgConfigured
              ? <CheckCircle2 size={12} className="text-trading-green flex-none" />
              : <AlertTriangle size={12} className="text-trading-yellow flex-none" />
            }
            <span className={tgConfigured ? 'text-trading-green' : 'text-trading-yellow'}>
              {tgConfigured ? '📱 Telegram 通知: 有効' : '📱 Telegram 未設定'}
            </span>
            {tgConfigured && tgLink && (
              <ExternalLink size={11} className="text-trading-green/70 flex-none" />
            )}
          </div>
        </div>
      )}

      {/* Content */}
      {visibleAlerts.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-3">
          <Bell size={28} className="text-trading-muted/40" />
          <p className="text-xs text-trading-muted text-center">アラートなし</p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-1.5">
          {visibleAlerts.map((alert) => (
            <div
              key={alert.id}
              onClick={() => navigateToSymbol(alert.symbol, alert.type)}
              className={clsx(
                'relative rounded-md border border-trading-border border-l-2 p-3 transition-all cursor-pointer',
                'hover:border-trading-blue/40 hover:brightness-110',
                SEVERITY_BORDER[alert.severity],
                SEVERITY_BG[alert.severity],
              )}
            >
              <button
                onClick={(e) => { e.stopPropagation(); dismissAlert(alert.id) }}
                className="absolute top-2 right-2 text-trading-muted hover:text-trading-text transition-colors"
              >
                <X size={11} />
              </button>
              <div className="flex items-center gap-1.5 mb-1 pr-4">
                <AlertIcon type={alert.type} />
                <span className="text-xs font-bold font-mono text-trading-text">{alert.symbol}</span>
                <ExternalLink size={10} className="text-trading-muted/50 flex-none" />
                <AlertBadge type={alert.type} severity={alert.severity} message={alert.message} />
              </div>
              <p className="text-[11px] text-trading-muted leading-snug mb-1.5">{alert.message}</p>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono text-trading-muted tabular-nums">
                  ${alert.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                </span>
                <span className="text-[10px] text-trading-muted">
                  {getRelativeTime(alert.timestamp)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
